import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import {
	CustomerAddressEditApi,
	CustomerAddressEditApi_InitData,
} from "src/api/generated/erp/sales/customer/api/customerAddressEditApi.ts";
import { CustomerAddress } from "src/api/generated/erp/db/types/tables/customerAddress.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { getCustomerAddressTypeLabels } from "src/api/generated/erp/db/types/enums/customerAddressType.ts";
import { CustomerAddressView } from "src/api/generated/erp/db/types/tables/customerAddressView.ts";
import { FormAddressAutofill } from "src/components/common/forms/fields/FormAddressAutofill.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { findDefaultVatHandlingType } from "src/components/views/erp/sales/salesUtils.ts";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerAddressFormProps extends FormCommonProps<number> {
	customerId: number;
	customerAddressId?: number;
	copyCustomerAddressSource?: CustomerAddressView;
}

export const CustomerAddressForm = (props: CustomerAddressFormProps) => {
	const { customerId, customerAddressId, copyCustomerAddressSource, onFormEdited, onCompleted } = props;
	return (
		<AsyncForm<CustomerAddressEditApi_InitData, CustomerAddress, number>
			columns={2}
			fetch={() =>
				CustomerAddressEditApi.getFormInitData({
					customerAddressId: customerAddressId,
				})
			}
			getDefaultValues={(data) => getDefaultValues({ initData: data })}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerAddress.form"),
				data: {
					customerId,
					customerAddressId: customerAddressId ?? null,
					copyCustomerAddressSourceId: copyCustomerAddressSource?.customerAddressId ?? null,
				},
			}}
			submit={async (values) => {
				if (customerAddressId == null) {
					return await CustomerAddressEditApi.insert({ customerAddress: values });
				} else {
					return await CustomerAddressEditApi.update({ customerAddress: values });
				}
			}}
			render={(params) => <FormContent {...params} {...props} />}
		/>
	);

	function getDefaultValues({ initData }: { initData: CustomerAddressEditApi_InitData }) {
		const customerAddress = initData.customerAddress;
		const defaultVatHandlingTypeId = findDefaultVatHandlingType(initData.vatHandlingTypeOptions)?.vatHandlingTypeId;
		if (customerAddress != undefined) {
			return {
				...customerAddress,
			};
		} else if (copyCustomerAddressSource != null) {
			return {
				...copyCustomerAddressSource,
				customerAddressId: undefined,
			};
		} else {
			return {
				customerId: customerId,
				country: "FINLAND",
				recipientName: "",
				vatHandlingTypeId: defaultVatHandlingTypeId,
			} as const;
		}
	}
};

interface FormContentProps
	extends CustomerAddressFormProps, AsyncFormContentParams<CustomerAddressEditApi_InitData, CustomerAddress> {}

const FormContent = ({ control, watch, setValue, data }: FormContentProps) => {
	return (
		<>
			<FormEnumSelectField
				control={control}
				name={"addressType"}
				label={i18n.t("type")}
				options={getCustomerAddressTypeLabels()}
				rules={requireRule()}
				disabled={data.customerAddress != null}
				spanGridColumns
			/>
			<FormTextField control={control} name={"name"} label={i18n.t("description")} spanGridColumns />
			{watch("addressType") === "DELIVERY" && (
				<FormTextField control={control} name={"recipientName"} label={i18n.t("recipient")} spanGridColumns />
			)}
			<FormAddressAutofill
				control={control}
				name={`address_1`}
				label={i18n.t("address_1")}
				rules={requireRule()}
				onSelectLocation={(location) => {
					setValue(`postalCode`, location.postcode);
					setValue(`city`, location.address_level2);
					return location.address_line1;
				}}
			/>
			<FormTextField control={control} name={"postalCode"} label={i18n.t("postal_code")} />
			<FormTextField control={control} name={"city"} label={i18n.t("city")} />
			<FormCountrySelectField label={i18n.t("country")} name={"country"} control={control} />
			<FormTextField control={control} name={"contact"} label={i18n.t("contact")} />
			<FormTextField control={control} name={"phone"} label={i18n.t("phone")} />
			<FormTextField control={control} name={"email"} label={i18n.t("email")} />
			{watch("addressType") === "DELIVERY" && (
				<>
					<FormSelectField
						control={control}
						name={"vatHandlingTypeId"}
						label={i18n.t("vat_handling_type")}
						options={data.vatHandlingTypeOptions}
						getOptionKey={(option) => option.vatHandlingTypeId}
						getOptionLabel={(option) => option.name}
						rules={requireRule()}
					/>
					<FormSelectField
						control={control}
						name={"transportRouteId"}
						label={i18n.t("transport_route")}
						options={data.transportRouteOptions}
						getOptionKey={(option) => option.transportRouteId}
						getOptionLabel={(option) => option.name}
					/>
					<FormSelectField
						control={control}
						name={"defaultDeliveryMethodId"}
						label={i18n.t("delivery_method")}
						options={data.deliveryMethodOptions}
						getOptionKey={(option) => option.deliveryMethodId}
						getOptionLabel={(option) => option.deliveryMethodCode}
					/>
				</>
			)}
		</>
	);
};
